import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { History, Save, Wrench } from "lucide-react";
import { DashboardShell } from "@/components/DashboardShell";
import { SUPER_ADMIN_NAV } from "@/lib/nav";
import { useAuth } from "@/lib/auth";
import {
  fetchMaintenance,
  saveMaintenance,
  logMaintenanceToggle,
  fetchMaintenanceAudit,
  MAINTENANCE_DEFAULTS,
  type MaintenanceConfig,
} from "@/lib/announcements";

export const Route = createFileRoute("/_authenticated/super-admin/maintenance")({
  head: () => ({
    meta: [
      { title: "System maintenance · Landoo Super Admin" },
      {
        name: "description",
        content:
          "Enable or disable maintenance mode, schedule an end time and review the maintenance audit log.",
      },
      { property: "og:title", content: "System maintenance · Landoo Super Admin" },
      {
        property: "og:description",
        content: "Control Landoo maintenance mode and the public maintenance message.",
      },
    ],
  }),
  component: MaintenancePage,
});

const input =
  "w-full rounded-lg border border-border bg-background px-3 py-2 text-xs outline-none focus:border-brand";

const toLocalInput = (iso?: string | null) => {
  if (!iso) return "";
  const d = new Date(iso);
  const off = d.getTimezoneOffset() * 60000;
  return new Date(d.getTime() - off).toISOString().slice(0, 16);
};

function MaintenancePage() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const { data } = useQuery({ queryKey: ["maintenance-config"], queryFn: fetchMaintenance });
  const { data: auditLog = [] } = useQuery({
    queryKey: ["maintenance-audit"],
    queryFn: fetchMaintenanceAudit,
  });
  const [cfg, setCfg] = useState<MaintenanceConfig>(MAINTENANCE_DEFAULTS);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (data) setCfg(data);
  }, [data]);

  const persist = async (next: MaintenanceConfig, isToggle: boolean) => {
    setSaving(true);
    try {
      await saveMaintenance(next);
      setCfg(next);
      if (isToggle && user?.id) {
        try {
          await logMaintenanceToggle(user.id, next, user.email);
          qc.invalidateQueries({ queryKey: ["maintenance-audit"] });
        } catch {
          /* audit failure must not block the toggle */
        }
      }
      qc.invalidateQueries({ queryKey: ["maintenance-config"] });
      toast.success(
        isToggle
          ? next.enabled
            ? "Maintenance mode enabled"
            : "Maintenance mode disabled"
          : "Saved",
      );
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Could not save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <DashboardShell title="System maintenance" role="Super Admin" items={SUPER_ADMIN_NAV}>
      <div className="grid gap-4 lg:grid-cols-2">
        <section className="rounded-xl border border-border bg-card p-4">
          <div className="flex items-start gap-3">
            <span className="rounded-lg bg-primary/10 p-2 text-primary">
              <Wrench className="h-4 w-4" />
            </span>
            <div className="min-w-0">
              <h2 className="text-sm font-semibold text-foreground">Maintenance mode</h2>
              <p className="mt-1 text-xs text-muted-foreground">
                While enabled, public users and customers/partners see the maintenance page. Admins
                and Super Admins can still sign in and use the platform.
              </p>
            </div>
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-2">
            <span
              className={`rounded-full px-2.5 py-1 text-[11px] font-semibold ${
                cfg.enabled
                  ? "bg-destructive/10 text-destructive"
                  : "bg-emerald-500/10 text-emerald-600"
              }`}
            >
              {cfg.enabled ? "Maintenance ON" : "Platform live"}
            </span>
            <button
              type="button"
              disabled={saving}
              onClick={() => persist({ ...cfg, enabled: !cfg.enabled }, true)}
              className="inline-flex items-center justify-center rounded-md bg-primary px-3 py-2 text-xs font-semibold text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-50"
            >
              {cfg.enabled ? "Disable maintenance mode" : "Enable maintenance mode"}
            </button>
          </div>

          <label className="mt-4 block">
            <span className="mb-1 block text-[11px] font-medium text-muted-foreground">
              Scheduled end (optional) — shown as a countdown on the maintenance page
            </span>
            <input
              type="datetime-local"
              className={input}
              value={toLocalInput(cfg.scheduled_end)}
              onChange={(e) =>
                setCfg({
                  ...cfg,
                  scheduled_end: e.target.value ? new Date(e.target.value).toISOString() : null,
                })
              }
            />
          </label>
        </section>

        <section className="rounded-xl border border-border bg-card p-4">
          <h2 className="text-sm font-semibold text-foreground">Maintenance page content</h2>
          <div className="mt-3 space-y-3">
            <label className="block">
              <span className="mb-1 block text-[11px] font-medium text-muted-foreground">
                Title
              </span>
              <input
                className={input}
                value={cfg.title}
                onChange={(e) => setCfg({ ...cfg, title: e.target.value })}
              />
            </label>
            <label className="block">
              <span className="mb-1 block text-[11px] font-medium text-muted-foreground">
                Message
              </span>
              <textarea
                rows={4}
                className={input}
                value={cfg.message}
                onChange={(e) => setCfg({ ...cfg, message: e.target.value })}
              />
            </label>
            <button
              type="button"
              disabled={saving}
              onClick={() => persist(cfg, false)}
              className="inline-flex items-center gap-2 rounded-md bg-primary px-3 py-2 text-xs font-semibold text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-50"
            >
              <Save className="h-3.5 w-3.5" /> Save content
            </button>
          </div>
        </section>

        <section className="rounded-xl border border-border bg-card p-4 lg:col-span-2">
          <h2 className="flex items-center gap-2 text-sm font-semibold text-foreground">
            <History className="h-4 w-4 text-primary" /> Maintenance audit log
          </h2>
          <p className="mt-1 text-xs text-muted-foreground">
            Every maintenance mode toggle, with who did it and when.
          </p>
          {auditLog.length === 0 ? (
            <p className="mt-3 text-xs text-muted-foreground">
              No maintenance toggles recorded yet.
            </p>
          ) : (
            <ul className="mt-3 space-y-2">
              {auditLog.map((entry) => (
                <li
                  key={entry.id}
                  className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-border p-3"
                >
                  <div className="min-w-0">
                    <span
                      className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                        entry.action === "maintenance_enabled"
                          ? "bg-destructive/10 text-destructive"
                          : "bg-emerald-500/10 text-emerald-600"
                      }`}
                    >
                      {entry.action === "maintenance_enabled" ? "Enabled" : "Disabled"}
                    </span>
                    <p className="mt-1 text-[11px] text-muted-foreground">
                      By {entry.metadata?.actor_email ?? entry.actor_id ?? "Unknown"}
                      {entry.metadata?.scheduled_end
                        ? ` · scheduled end ${new Date(entry.metadata.scheduled_end).toLocaleString()}`
                        : ""}
                    </p>
                  </div>
                  <span className="text-[11px] text-muted-foreground">
                    {new Date(entry.created_at).toLocaleString()}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </DashboardShell>
  );
}
